import { Injectable, inject } from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '@environments/environment';
import {Observable} from 'rxjs';
import {timeout} from 'rxjs/operators';

const REQUEST_TIMEOUT = 60000;

export type MonitoringLevel = 'normal' | 'warning' | 'error' | 'paused' | 'disabled';
// Active: checked, Normal / Warning / Error; Paused: resumes by itself; Disabled: the project is over,
// or a person disabled its monitoring.
export type MonitoringState = 'active' | 'paused' | 'disabled';
export type MonitoringIssueLevel = 'error' | 'warning';
export type MonitoringSiteStatus = 'pending' | 'approved' | 'declined';
export type MonitoringSiteSource = 'registration' | 'load' | 'manual';
// open: open and unacknowledged; acknowledged: open and acknowledged (still checked, never emailed);
// fixed, ignored, recovered and false_alarm: closed that way; disabled: open when monitoring was
// disabled. An ignored issue's (or a false alarm's) mutedUntil says whether its mute lasts.
export type MonitoringIssueState = 'open' | 'acknowledged' | 'fixed' | 'ignored' | 'recovered' | 'false_alarm' | 'disabled';
export type MonitoringCloseReason = 'recovered' | 'fixed' | 'ignored' | 'false_alarm' | 'disabled';
// What an acknowledgement does: Fixed, Ignore and False alarm close the issue, Acknowledge keeps it
// open; Ignore and False alarm mute the check (never for a manual issue).
export type MonitoringResolution = 'fixed' | 'acknowledge' | 'ignore' | 'false_alarm';
// Who raised an issue: a check, or a person who logged it from the page.
export type MonitoringIssueType = 'check' | 'manual';
export type MonitoringMute = 'week' | 'month' | 'forever';
export type MonitoringHistoryType = 'issues' | 'checks' | 'events';

export interface MonitoringUserRef {
  id: number;
  name: string | null;
}

export interface MonitoringReason {
  code: string;
  message: string;
  // What would resume monitoring for this reason, in the server's words; null when nothing the user does will.
  hint: string | null;
}

export interface MonitoringSite {
  id: number;
  host: string;
  status: MonitoringSiteStatus;
  source: MonitoringSiteSource;
  // Whether the site counts as expected: approved, or pending after a registration there. A
  // pending site seen only through widget loads is not, until someone approves it.
  expected?: boolean;
  // Whether its widget loads count as unexpected for the unexpected-site rules, by the server's
  // own judge: declined, or seen only through widget loads and proposed after the first check
  // settled the sites. False before that check, and for sites found before it. Absent from an
  // older payload.
  countsAsUnexpected?: boolean;
  firstSeenAt: string | null;
  registrations: number | null;
  loads7d: number | null;
  samplePath: string | null;
  decidedAt: string | null;
  decidedBy: MonitoringUserRef | null;
  // Set when the check approved the site by itself (decidedBy is then null): why, as a code
  // (baseline-registration, baseline-loads, same-domain, project-url, platform-host) and in the
  // server's words. Null for a person's decision; absent from an older payload.
  autoReason?: string | null;
  autoReasonText?: string | null;
  note: string | null;
  // A pending site seen only through widget loads: in how many browsers on how many days the widget
  // reported running there, and whether that is too few to ask anyone about it yet (a preview, a
  // staging copy, one tester). Null for other sites and before a check that read it.
  audience?: {browsers: number; days: number; lowAudience: boolean} | null;
}

export interface MonitoringEvidenceItem {
  label: string;
  value: string;
}

export interface MonitoringIssue {
  id: number;
  kind: string;
  level: MonitoringIssueLevel;
  title: string;
  explanation: string;
  // The numbers that tripped the rule; keys are server-chosen.
  evidence: {[key: string]: number | string | boolean | null} | null;
  // The evidence in the server's words (the alert email prints the same lines); absent from older servers.
  evidenceItems?: MonitoringEvidenceItem[];
  state: MonitoringIssueState;
  // A call to action link finding: acknowledging it once confirms the links it names, and closes it.
  confirmable?: boolean;
  openedAt: string;
  lastSeenAt: string | null;
  closedAt: string | null;
  closeReason: MonitoringCloseReason | null;
  acknowledgedAt: string | null;
  acknowledgedBy: MonitoringUserRef | null;
  note: string | null;
  mutedUntil: string | null;
  // A check's issue, or one a person logged (`manual`, with who did); absent from older servers.
  type?: MonitoringIssueType;
  reportedBy?: MonitoringUserRef | null;
}

/** One window of §3 metrics. A null count means the metric is unavailable (its module is off). */
export interface MonitoringMetrics {
  loads: number | null;
  failedLoads: number | null;
  sessions: number | null;
  shown: number | null;
  interactions: number | null;
  opens: number | null;
  shares: number | null;
  registrations: number | null;
  registerFailures: number | null;
  conversions: number | null;
  conversionsByTracker: {[tracker: string]: number} | null;
}

export interface MonitoringHostLoads {
  host: string;
  loads: number | null;
  failed: number | null;
  expected: boolean;
  siteStatus: MonitoringSiteStatus | null;
}

export interface MonitoringCheckRule {
  kind: string;
  level: MonitoringIssueLevel;
  title: string;
  description: string;
  thresholds: {[name: string]: number | string | boolean | null} | null;
  available: boolean;
  unavailableReason: string | null;
  mutedUntil: string | null;
}

export interface ProjectMonitoringData {
  project: {id: number; title: string; timezone: string | null};
  monitoring: {
    active: boolean;
    // Absent from an older payload (read as active or paused from `active`).
    state?: MonitoringState;
    // A person's decision to disable it: who, when, why. Null otherwise.
    disabled?: {at: string | null; by: MonitoringUserRef | null; note: string | null} | null;
    reasons: MonitoringReason[];
    lastCheckedAt: string | null;
    nextCheckAt: string | null;
    // The call to action link checks, listed after the integration's own.
    linkChecks?: MonitoringCheckRule[];
    runNow: {queued: boolean; availableAt: string | null};
  };
  status: {level: MonitoringLevel; since: string | null};
  permissions: {manage: boolean};
  sites: MonitoringSite[];
  // `sites` is cut at a bound: how many there are in all, listed and not, how many are pending
  // (listed or not), and the new sites the latest check held back from proposing while too many
  // were pending (null before a check counted them). Absent from an older payload; `pending` too.
  sitesSummary?: {total: number; listed: number; notListed: number; pending?: number; heldBack: number | null};
  issues: {open: MonitoringIssue[]; recent: MonitoringIssue[]};
  // Null before the first check.
  metrics: {
    checkedAt: string;
    windows: {day: MonitoringMetrics; week: MonitoringMetrics; previousWeek: MonitoringMetrics};
    loadsByHost: MonitoringHostLoads[];
    // The window loadsByHost (and each site's loads7d) covers, in hours.
    loadsByHostWindowHours: number;
    // Since the first registration: referrals the IP address alone credited from addresses that
    // credited several advocates (a venue, office or mobile network). Null when not read.
    sharedNetworks?: {
      referralsFromSharedAddresses: number | null;
      sharedAddresses: number | null;
      mostAdvocatesOnOneAddress: number | null;
    } | null;
  } | null;
  checks: MonitoringCheckRule[];
}

export interface MonitoringCheckRow {
  id: number;
  checkedAt: string;
  trigger: 'schedule' | 'manual';
  triggeredBy: MonitoringUserRef | null;
  monitoringActive: boolean;
  status: MonitoringLevel;
  issueCount: number;
  durationMs: number | null;
}

export interface MonitoringEventRow {
  id: number;
  occurredAt: string;
  kind: string;
  actor: MonitoringUserRef | null;
  issueId: number | null;
  siteId: number | null;
  summary: string;
  details: unknown;
}

/** A row of History > Issues: the Issue shape plus how many checks found it. */
export interface MonitoringIssueHistoryRow extends MonitoringIssue {
  seenCount: number;
}

/** History > Issues filters; an empty value (or none) is "all". */
export interface MonitoringIssueHistoryFilters {
  level?: MonitoringIssueLevel | '';
  state?: MonitoringIssueState | '';
}

export interface MonitoringHistoryPage<T> {
  rows: T[];
  page: number;
  perPage: number;
  total: number;
}

export interface MonitoringResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/** Admin > Projects Monitoring: the projects of one monitoring state the user can access. */
export type ProjectsMonitoringStatus = 'error' | 'warning' | 'normal' | 'all';

export interface ProjectsMonitoringListParams {
  page: number;
  perPage: number;
  // Active when left out.
  state?: MonitoringState;
  // Active only: errors and warnings when left out, one of them, normal, or all.
  status?: ProjectsMonitoringStatus | '';
  organizationId?: number | null;
  clientId?: number | null;
  // Project title or code.
  search?: string;
}

export interface ProjectsMonitoringRow {
  projectId: number;
  projectTitle: string;
  projectCode: string | null;
  clientId: number | null;
  clientName: string | null;
  organizationId: number | null;
  organizationName: string | null;
  status: MonitoringLevel;
  statusSince: string | null;
  // Unacknowledged open issues per level, the ones the status counts.
  openErrors: number;
  openWarnings: number;
  // Acknowledged-open issues of either level; absent from older servers.
  acknowledgedOpen?: number;
  lastCheckedAt: string | null;
  monitoringActive: boolean;
  // Absent from older servers; there every row is active.
  state?: MonitoringState;
  // Paused and Disabled: why, as the last check stored it.
  reasons?: {code: string; message: string | null}[];
  // A person's decision to disable it.
  disabled?: {at: string | null; by: MonitoringUserRef | null; note: string | null} | null;
}

export interface ProjectsMonitoringFilters {
  organizations: {id: number; name: string}[];
  clients: {id: number; name: string; organizationId: number | null}[];
}

export interface MonitoringAcknowledgement {
  resolution: MonitoringResolution;
  mute?: MonitoringMute;
  note?: string;
}

/** An issue logged by hand: what was seen, at the level chosen. */
export interface MonitoringIssueReport {
  level: MonitoringIssueLevel;
  description: string;
}

/** A check of every monitored project, queued from Admin > Projects Monitoring's Refresh. */
export type ProjectsMonitoringSweepState = 'queued' | 'running' | 'done' | 'failed';

export interface ProjectsMonitoringSweep {
  // Null when the run finished before the server could read it back.
  jobId: string | null;
  state?: ProjectsMonitoringSweepState;
  // The run's counts once it is done.
  summary?: {total: number; checked: number; paused: number; disabled?: number; failed: number; skipped: number} | null;
}

@Injectable({providedIn: 'root'})
export class ProjectMonitoringService {
  private http = inject(HttpClient);

  private root = environment.apiUrl + '/api/v1/project-monitoring';
  private base = this.root + '/';

  get(projectId: number): Observable<MonitoringResponse<ProjectMonitoringData>> {
    return this.http.get<MonitoringResponse<ProjectMonitoringData>>(this.url(projectId)).pipe(timeout(REQUEST_TIMEOUT));
  }

  history(projectId: number, type: 'checks', page?: number, perPage?: number): Observable<MonitoringResponse<MonitoringHistoryPage<MonitoringCheckRow>>>;
  history(projectId: number, type: 'events', page?: number, perPage?: number): Observable<MonitoringResponse<MonitoringHistoryPage<MonitoringEventRow>>>;
  history(projectId: number, type: 'checks' | 'events', page = 1, perPage = 20): Observable<MonitoringResponse<MonitoringHistoryPage<any>>> {
    const params = new HttpParams().set('type', type).set('page', String(page)).set('perPage', String(perPage));
    return this.http.get<MonitoringResponse<MonitoringHistoryPage<any>>>(this.url(projectId, 'history'), {params})
      .pipe(timeout(REQUEST_TIMEOUT));
  }

  /** History > Issues: every error and warning of the project, newest first. */
  issueHistory(projectId: number, filters: MonitoringIssueHistoryFilters = {}, page = 1, perPage = 20):
  Observable<MonitoringResponse<MonitoringHistoryPage<MonitoringIssueHistoryRow>>> {
    let params = new HttpParams().set('type', 'issues').set('page', String(page)).set('perPage', String(perPage));
    if (filters.level) {
      params = params.set('level', filters.level);
    }
    if (filters.state) {
      params = params.set('state', filters.state);
    }
    return this.http.get<MonitoringResponse<MonitoringHistoryPage<MonitoringIssueHistoryRow>>>(this.url(projectId, 'history'), {params})
      .pipe(timeout(REQUEST_TIMEOUT));
  }

  /** Admin > Projects Monitoring: one page of the projects in error or warning. */
  list(query: ProjectsMonitoringListParams): Observable<MonitoringResponse<MonitoringHistoryPage<ProjectsMonitoringRow>>> {
    let params = new HttpParams().set('page', String(query.page)).set('perPage', String(query.perPage));
    // Only the filters in use go, so the server's defaults (active, both levels, every project) apply.
    if (query.state && query.state !== 'active') {
      params = params.set('state', query.state);
    } else if (query.status) {
      // The status narrows the active projects only.
      params = params.set('status', query.status);
    }
    if (query.organizationId) {
      params = params.set('organizationId', String(query.organizationId));
    }
    if (query.clientId) {
      params = params.set('clientId', String(query.clientId));
    }
    const search = (query.search || '').trim();
    if (search) {
      params = params.set('search', search);
    }
    return this.http.get<MonitoringResponse<MonitoringHistoryPage<ProjectsMonitoringRow>>>(this.root, {params})
      .pipe(timeout(REQUEST_TIMEOUT));
  }

  /** The organization and client choices of the admin list, from the same scoped set (of one state). */
  listFilters(state?: MonitoringState): Observable<MonitoringResponse<ProjectsMonitoringFilters>> {
    const params = state && state !== 'active' ? new HttpParams().set('state', state) : undefined;
    return this.http.get<MonitoringResponse<ProjectsMonitoringFilters>>(this.root + '/filters', params ? {params} : {})
      .pipe(timeout(REQUEST_TIMEOUT));
  }

  runNow(projectId: number) {
    return this.post(projectId, 'run', {});
  }

  /** A person's "Disable monitoring" (a test or demo project, say), with an optional note. */
  disable(projectId: number, note?: string) {
    return this.post(projectId, 'disable', this.withNote({}, note));
  }

  /** Lifts a person's decision to disable it; the state then follows the project's dates and settings. */
  enable(projectId: number) {
    return this.post(projectId, 'enable', {});
  }

  /** Queue the check of every monitored project (Super Admin and Admin), or join the one queued. */
  sweep(): Observable<MonitoringResponse<ProjectsMonitoringSweep>> {
    return this.http.post<MonitoringResponse<ProjectsMonitoringSweep>>(this.root + '/sweep', {}).pipe(timeout(REQUEST_TIMEOUT));
  }

  /** Where a queued check of every project has got to. */
  sweepState(jobId: string): Observable<MonitoringResponse<ProjectsMonitoringSweep>> {
    return this.http.get<MonitoringResponse<ProjectsMonitoringSweep>>(this.root + '/sweep/' + encodeURIComponent(jobId))
      .pipe(timeout(REQUEST_TIMEOUT));
  }

  addSite(projectId: number, host: string) {
    return this.post(projectId, 'sites', {host});
  }

  approveSite(projectId: number, siteId: number, note?: string) {
    return this.post(projectId, `sites/${siteId}/approve`, this.withNote({}, note));
  }

  declineSite(projectId: number, siteId: number, note?: string) {
    return this.post(projectId, `sites/${siteId}/decline`, this.withNote({}, note));
  }

  replaceSite(projectId: number, siteId: number, host: string, note?: string) {
    return this.post(projectId, `sites/${siteId}/replace`, this.withNote({host}, note));
  }

  acknowledgeIssue(projectId: number, issueId: number, body: MonitoringAcknowledgement) {
    return this.post(projectId, `issues/${issueId}/acknowledge`, this.ackBody(body));
  }

  acknowledgeLevel(projectId: number, level: MonitoringIssueLevel, body: MonitoringAcknowledgement) {
    return this.post(projectId, 'issues/acknowledge', {level, ...this.ackBody(body)});
  }

  /** Log an issue by hand; the server keeps it in History > Issues with who logged it. */
  logIssue(projectId: number, report: MonitoringIssueReport) {
    return this.post(projectId, 'issues', {level: report.level, description: (report.description || '').trim()});
  }

  private post(projectId: number, path: string, body: object): Observable<MonitoringResponse<ProjectMonitoringData>> {
    return this.http.post<MonitoringResponse<ProjectMonitoringData>>(this.url(projectId, path), body).pipe(timeout(REQUEST_TIMEOUT));
  }

  private url(projectId: number, path?: string): string {
    return this.base + encodeURIComponent(String(projectId)) + (path ? '/' + path : '');
  }

  // Optional fields go only when set, so the server's Joi schema sees no empty strings.
  private withNote<T extends object>(body: T, note?: string): T & {note?: string} {
    const trimmed = (note || '').trim();
    return trimmed ? {...body, note: trimmed} : body;
  }

  private ackBody(body: MonitoringAcknowledgement): MonitoringAcknowledgement {
    const out: MonitoringAcknowledgement = {resolution: body.resolution};
    // The server takes a mute with Ignore and False alarm only.
    if ((body.resolution === 'ignore' || body.resolution === 'false_alarm') && body.mute) {
      out.mute = body.mute;
    }
    return this.withNote(out, body.note);
  }
}
